import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
  useRef,
} from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import "./ReservationManager.css";
import ReservationCalendar from "./ReservationCalendar";
import TimeSelection from "./TimeSelection";
import ConfirmationScreen from "./ConfirmationScreen";
import { motion, AnimatePresence } from "framer-motion";
import { DateTime } from "luxon";
import useIsPhone from "MainApp/assets/utils/hooks/useIsPhone";
import { scrollToTop } from "MainApp/assets/visual/styles/globalStyles/globalJsxStyles";
import { t } from "i18next";
// import useAxios from "MainApp/assets/utils/hooks/useAxios";
import { AuthContext } from "MainApp/assets/utils/context/AuthContext";
import { to } from "react-spring";
// import BlockerComponent from "./BackBlocker";

// Creamos un contexto para compartir la fecha de la cita
const AppointmentContext = createContext();
//  const location   = useLocation();          // ← ¡aquí!
//   const navigate   = useNavigate();
//  const searchParams  = new URLSearchParams(location.search);
const searchParams = new URLSearchParams(window.location.search);
const parsePositiveParam = (value) => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};
const urlDuration =
  Number(searchParams.get("duration") || searchParams.get("dur")) || null;
const urlPeopleCount = parsePositiveParam(
  searchParams.get("people") ||
    searchParams.get("persons") ||
    searchParams.get("pax")
);
const urlPeopleMin = parsePositiveParam(
  searchParams.get("peopleMin") || searchParams.get("min_people")
);
const urlPeopleMax = parsePositiveParam(
  searchParams.get("peopleMax") || searchParams.get("max_people")
);

const normalizePeoplePrices = (ranges) =>
  (Array.isArray(ranges) ? ranges : []).map((range) => ({
    min_people: Number(range?.min_people ?? range?.minPeople ?? 1) || 1,
    max_people: (() => {
      const rawMax = range?.max_people ?? range?.maxPeople;
      if (rawMax === "" || rawMax == null) return null;
      const parsed = Number(rawMax);
      return Number.isFinite(parsed) ? parsed : null;
    })(),
    price: Number(range?.price ?? 0) || 0,
  }));

const normalizeDurations = (durations) =>
  (Array.isArray(durations) ? durations : []).map((d) => ({
    ...d,
    duration: Number(d?.duration ?? 0) || 0,
    price: Number(d?.price ?? 0) || 0,
    stripe_price_id: d?.stripe_price_id ?? d?.stripePriceId ?? "",
    people_prices: normalizePeoplePrices(d?.people_prices ?? d?.peoplePrices),
  }));

const hasInvalidPeopleRanges = (durations) =>
  (Array.isArray(durations) ? durations : []).some((d) => {
    const ranges = Array.isArray(d?.people_prices) ? d.people_prices : [];
    if (ranges.length < 2) return false;

    return ranges.some((range, idx) => {
      const isNotLast = idx < ranges.length - 1;
      if (!isNotLast) return false;
      const maxPeople = Number(range?.max_people);
      return !Number.isFinite(maxPeople);
    });
  });

const TOURS_CACHE_STORAGE_KEY = "toursCache:v2";
const TOURS_CACHE_MAX_AGE_MS = 15 * 60 * 1000;

const normalizeTourData = (raw, fallbackSlug = "") => {
  if (!raw) return null;

  const durations = normalizeDurations(raw.durations);
  const queryDuration = Number(urlDuration ?? 0) || null;
  const selectedDuration =
    Number(
      queryDuration ??
        raw.selectedDuration ??
        raw.duration ??
        durations?.[0]?.duration ??
        0
    ) || 0;
  const selectedDurationData =
    durations.find((d) => Number(d.duration) === selectedDuration) ||
    durations[0] ||
    null;

  const resolvedPrice =
    Number(selectedDurationData?.price ?? raw.price ?? raw.priceMin ?? 0) || 0;
  const sprice =
    raw.sprice ??
    selectedDurationData?.stripe_price_id ??
    raw.stripe_price_id ??
    (durations.length === 1 ? durations[0]?.stripe_price_id : "") ??
    "";
  const usesPeopleAndTimesPricing = Boolean(
    raw.usesPeopleAndTimesPricing ?? raw.uses_people_and_times_pricing
  );
  const frontendUsesPeoplePriceTiers = Boolean(
    raw.frontendUsesPeoplePriceTiers ??
      raw.frontend_uses_people_price_tiers
  );

  const querySelectedPeopleRange =
    urlPeopleMin || urlPeopleMax || urlPeopleCount
      ? {
          min_people: urlPeopleMin ?? urlPeopleCount ?? 1,
          max_people: urlPeopleMax ?? urlPeopleCount ?? null,
        }
      : null;
  const selectedPeopleRangeRaw =
    raw.selectedPeopleRange ??
    raw.selected_people_range ??
    querySelectedPeopleRange ??
    null;
  const selectedPeopleRange = selectedPeopleRangeRaw
    ? {
        min_people:
          Number(
            selectedPeopleRangeRaw?.min_people ??
              selectedPeopleRangeRaw?.minPeople ??
              1
          ) || 1,
        max_people: (() => {
          const rawMax =
            selectedPeopleRangeRaw?.max_people ??
            selectedPeopleRangeRaw?.maxPeople;
          if (rawMax === "" || rawMax == null) return null;
          const parsed = Number(rawMax);
          return Number.isFinite(parsed) ? parsed : null;
        })(),
        price: Number(selectedPeopleRangeRaw?.price ?? 0) || 0,
      }
    : null;
  const selectedPeopleCountFromRange = (() => {
    if (!selectedPeopleRange) return 0;
    const maxPeople = Number(selectedPeopleRange.max_people);
    if (Number.isFinite(maxPeople) && maxPeople > 0) return maxPeople;
    return Math.max(1, Number(selectedPeopleRange.min_people ?? 1) || 1);
  })();
  const selectedPeopleCount =
    Number(raw.selectedPeopleCount ?? raw.selected_people_count ?? 0) ||
    selectedPeopleCountFromRange ||
    urlPeopleCount ||
    null;

  return {
    ...raw,
    id: raw.id || raw.tourUUID || raw.tour_uuid || null,
    title: raw.title || raw.name || "",
    slug: raw.slug || fallbackSlug || "",
    price: resolvedPrice,
    duration: selectedDuration,
    sprice,
    durations,
    vehiclesSmall: Boolean(
      raw.vehiclesSmall ?? raw.show_vehicles_as_lowest_capacity
    ),
    personsInsteadTime: Boolean(
      raw.personsInsteadTime ?? raw.uses_people_instead_of_times
    ),
    selectedPeopleRange,
    selected_people_range: selectedPeopleRange,
    selectedPeopleCount,
    selected_people_count: selectedPeopleCount,
    usesPeopleAndTimesPricing,
    uses_people_and_times_pricing: usesPeopleAndTimesPricing,
    frontendUsesPeoplePriceTiers,
    frontend_uses_people_price_tiers: frontendUsesPeoplePriceTiers,
    preferLowerPeoplePriceTier: Boolean(
      raw.preferLowerPeoplePriceTier ?? raw.prefer_lower_people_price_tier
    ),
    prefer_lower_people_price_tier: Boolean(
      raw.preferLowerPeoplePriceTier ?? raw.prefer_lower_people_price_tier
    ),
    customPickupPolicy:
      raw.customPickupPolicy ?? raw.custom_pickup_policy ?? "inherit",
    custom_pickup_policy:
      raw.customPickupPolicy ?? raw.custom_pickup_policy ?? "inherit",
    customPickupFeeAmount:
      raw.customPickupFeeAmount ?? raw.custom_pickup_fee_amount ?? null,
    custom_pickup_fee_amount:
      raw.customPickupFeeAmount ?? raw.custom_pickup_fee_amount ?? null,
    customPickupRadiusKm:
      raw.customPickupRadiusKm ?? raw.custom_pickup_radius_km ?? null,
    custom_pickup_radius_km:
      raw.customPickupRadiusKm ?? raw.custom_pickup_radius_km ?? null,
  };
};

const AppointmentProvider = ({ children }) => {
  const [appointmentDate, setAppointmentDate] = useState(null);
  return (
    <AppointmentContext.Provider
      value={{ appointmentDate, setAppointmentDate }}
    >
      {/* <BlockerComponent/> */}
      {children}
    </AppointmentContext.Provider>
  );
};

const isEmbeddedReservationFlow = () =>
  typeof window !== "undefined" && window.parent && window.parent !== window;

const createReservationStepHistoryUrl = (nextStep, entryId) => {
  const url = new URL(window.location.href);
  url.hash =
    Number(nextStep) === 0
      ? `reservation-close-sentinel-${entryId}`
      : `reservation-step-${nextStep}-${entryId}`;
  return url.href;
};

const ReservationManager = () => {
  // Obtenemos axiosInstance desde el AuthContext
  const { axiosInstance } = useContext(AuthContext);
  const isPhone = useIsPhone();
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();

  // Intentamos obtener el tour desde el state de navegación (si se pasó)
  const tourFromState = normalizeTourData(
    location.state?.tour || location.state?.newTour,
    slug
  );
  // console.log("tourFromState", tourFromState);
  const [tour, setTour] = useState(tourFromState || null);
  const initialUsesPeopleAndTimesPricing = Boolean(
    tour?.usesPeopleAndTimesPricing ?? tour?.uses_people_and_times_pricing
  );
  const initialFrontendUsesPeoplePriceTiers = Boolean(
    tour?.frontendUsesPeoplePriceTiers ??
      tour?.frontend_uses_people_price_tiers
  );
  const initialSelectedPeopleCount = Boolean(
    initialUsesPeopleAndTimesPricing && initialFrontendUsesPeoplePriceTiers
  )
    ? Number(tour?.selectedPeopleCount ?? tour?.selected_people_count ?? 0) || 0
    : 0;

  // Paso actual del flujo (1: calendario, 2: selección de hora, 3: confirmación)
  const [step, setStep] = useState(1);
  const stepRef = useRef(1);
  const historyEntryIdRef = useRef(0);

  // Fechas y horas seleccionadas
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  const writeStepHistory = useCallback((nextStep, { mode = "replace" } = {}) => {
    const historyMethod = mode === "push" ? "pushState" : "replaceState";
    historyEntryIdRef.current += 1;
    const historyUrl = isEmbeddedReservationFlow()
      ? createReservationStepHistoryUrl(nextStep, historyEntryIdRef.current)
      : window.location.href;

    window.history[historyMethod](
      {
        ...(window.history.state || {}),
        reservationCloseSentinel: false,
        step: nextStep,
        reservationStep: nextStep,
      },
      "",
      historyUrl
    );
  }, []);

  const requestParentClose = useCallback(() => {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage("modalClosed", "*");
      return true;
    }

    return false;
  }, []);

  const goToStep = useCallback(
    (nextStep, { historyMode = "replace" } = {}) => {
      const safeStep = Math.min(3, Math.max(1, Number(nextStep) || 1));

      if (safeStep <= 2) {
        setSelectedTime(null);
      }

      writeStepHistory(safeStep, { mode: historyMode });
      setStep(safeStep);
    },
    [writeStepHistory]
  );

  const goToPreviousStep = useCallback(({ preferBrowserHistory = false } = {}) => {
    const currentStep = stepRef.current;
    if (currentStep <= 1) {
      requestParentClose();
      return;
    }

    if (
      preferBrowserHistory &&
      window.history.state?.reservationStep === currentStep
    ) {
      window.history.back();
      return;
    }

    goToStep(currentStep - 1);
  }, [goToStep, requestParentClose]);

  // Datos del formulario (reserva)
  const [formData, setFormData] = useState({
    bookingStart: 0,
    bookingEnd: 0,
    tour: "",
    client: null,
    tourUUID: null,
    customPickUp: "",
    customPickup: "",
    customPickupRequested: false,
    customPickupPolicy:
      tour?.customPickupPolicy ?? tour?.custom_pickup_policy ?? "inherit",
    custom_pickup_policy:
      tour?.customPickupPolicy ?? tour?.custom_pickup_policy ?? "inherit",
    customPickupFeeAmount:
      tour?.customPickupFeeAmount ?? tour?.custom_pickup_fee_amount ?? null,
    custom_pickup_fee_amount:
      tour?.customPickupFeeAmount ?? tour?.custom_pickup_fee_amount ?? null,
    customPickupRadiusKm:
      tour?.customPickupRadiusKm ?? tour?.custom_pickup_radius_km ?? null,
    custom_pickup_radius_km:
      tour?.customPickupRadiusKm ?? tour?.custom_pickup_radius_km ?? null,
    priceUnit: 2,
    sprice: null,
    persons: initialSelectedPeopleCount,
    phone: 0,
    adults: initialSelectedPeopleCount,
    minorsTotal: 0,
    minors: false,
    minors2to5: 0,
    minors6to10: 0,
    minors11to17: 0,
    tuktukModels: [],
    totalUnits: 0,
    country: "",
    name: "",
    email: "",
    hasMinors: false,
    vehiclesSmall: tour?.vehiclesSmall,
    personsInsteadTime: tour?.personsInsteadTime,
    selectedPeopleRange:
      tour?.selectedPeopleRange ?? tour?.selected_people_range ?? null,
    selected_people_range:
      tour?.selectedPeopleRange ?? tour?.selected_people_range ?? null,
    selectedPeopleCount:
      Number(tour?.selectedPeopleCount ?? tour?.selected_people_count ?? 0) ||
      null,
    selected_people_count:
      Number(tour?.selectedPeopleCount ?? tour?.selected_people_count ?? 0) ||
      null,
    durations: tour?.durations || [],
    selectedDuration: tour?.duration || null,
    usesPeopleAndTimesPricing:
      tour?.usesPeopleAndTimesPricing ||
      tour?.uses_people_and_times_pricing ||
      false,
    frontend_uses_people_price_tiers:
      tour?.frontendUsesPeoplePriceTiers ||
      tour?.frontend_uses_people_price_tiers ||
      false,
    frontendUsesPeoplePriceTiers:
      tour?.frontendUsesPeoplePriceTiers ||
      tour?.frontend_uses_people_price_tiers ||
      false,
    preferLowerPeoplePriceTier:
      tour?.preferLowerPeoplePriceTier ||
      tour?.prefer_lower_people_price_tier ||
      false,
  });

  console.log("tourfgty", tour);
  console.log("tourfgfor,hty", formData);
  console.log("tourFromState,hty", tourFromState);

  // Extraemos el setter del AppointmentContext
  const { setAppointmentDate } = useContext(AppointmentContext);
  // console.log("formDatatest");

  // useEffect(() => {
  //   console.log(formData);
  // }, [formData]);

  // =============================================
  // Actualizamos formData cuando se obtiene el tour
  // =============================================
  useEffect(() => {
    if (tour) {
      console.log("Tour a FORM actualizado:", tour);
      const tourSelectedPeopleCount =
        Number(tour?.selectedPeopleCount ?? tour?.selected_people_count ?? 0) ||
        0;
      const tourUsesPeopleAndTimesPricing = Boolean(
        tour?.usesPeopleAndTimesPricing ?? tour?.uses_people_and_times_pricing
      );
      const tourFrontendUsesPeoplePriceTiers = Boolean(
        tour?.frontendUsesPeoplePriceTiers ??
          tour?.frontend_uses_people_price_tiers
      );
      const tourUsesPeoplePriceTiersInFrontend = Boolean(
        tourUsesPeopleAndTimesPricing && tourFrontendUsesPeoplePriceTiers
      );
      setFormData((prev) => ({
        ...prev,
        tour: tour.title || "",
        tourUUID: tour.id || null,
        priceUnit: tour.price || 2,
        sprice: tour.sprice || null,
        vehiclesSmall: tour?.vehiclesSmall,
        personsInsteadTime: tour?.personsInsteadTime,
        selectedPeopleRange:
          tour?.selectedPeopleRange ?? tour?.selected_people_range ?? null,
        selected_people_range:
          tour?.selectedPeopleRange ?? tour?.selected_people_range ?? null,
        selectedPeopleCount:
          Number(tour?.selectedPeopleCount ?? tour?.selected_people_count ?? 0) ||
          null,
        selected_people_count:
          Number(tour?.selectedPeopleCount ?? tour?.selected_people_count ?? 0) ||
          null,
        durations: Array.isArray(tour?.durations) ? tour.durations : [],
        selectedDuration: tour?.duration || null,
        usesPeopleAndTimesPricing:
          tour?.usesPeopleAndTimesPricing ||
          tour?.uses_people_and_times_pricing ||
          false,
        frontend_uses_people_price_tiers:
          tour?.frontendUsesPeoplePriceTiers ||
          tour?.frontend_uses_people_price_tiers ||
          false,
        frontendUsesPeoplePriceTiers:
          tour?.frontendUsesPeoplePriceTiers ||
          tour?.frontend_uses_people_price_tiers ||
          false,
        preferLowerPeoplePriceTier:
          tour?.preferLowerPeoplePriceTier ||
          tour?.prefer_lower_people_price_tier ||
          false,
        customPickupPolicy:
          tour?.customPickupPolicy ?? tour?.custom_pickup_policy ?? "inherit",
        custom_pickup_policy:
          tour?.customPickupPolicy ?? tour?.custom_pickup_policy ?? "inherit",
        customPickupFeeAmount:
          tour?.customPickupFeeAmount ?? tour?.custom_pickup_fee_amount ?? null,
        custom_pickup_fee_amount:
          tour?.customPickupFeeAmount ?? tour?.custom_pickup_fee_amount ?? null,
        customPickupRadiusKm:
          tour?.customPickupRadiusKm ?? tour?.custom_pickup_radius_km ?? null,
        custom_pickup_radius_km:
          tour?.customPickupRadiusKm ?? tour?.custom_pickup_radius_km ?? null,
        persons:
          tourUsesPeoplePriceTiersInFrontend && tourSelectedPeopleCount > 0
            ? tourSelectedPeopleCount
            : Number(prev.persons || 0),
        adults:
          tourUsesPeoplePriceTiersInFrontend && tourSelectedPeopleCount > 0
            ? tourSelectedPeopleCount
            : Number(prev.adults || 0),
        minorsTotal:
          tourUsesPeoplePriceTiersInFrontend && tourSelectedPeopleCount > 0
            ? 0
            : prev.minorsTotal,
      }));
    }
  }, [tour]);

  // Sincroniza el tour en location.state para que el header global
  // (TourContainer) pueda mostrar el nombre del tour también en reserva.
  useEffect(() => {
    if (!tour) return;

    const stateTour = location.state?.tour;
    const stateTitle = location.state?.title;
    if (
      stateTour?.id === tour.id &&
      stateTour?.title === tour.title &&
      stateTitle === tour.title
    ) {
      return;
    }

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: {
        ...(location.state || {}),
        title: tour.title || "",
        tour,
      },
    });
  }, [tour, navigate, location.pathname, location.search, location.state]);

  // =============================================
  // Obtener el tour (server o caché)
  // =============================================
  useEffect(() => {
    let isMounted = true;

    const getTourFromCache = (slug) => {
      try {
        const cachedTours =
          JSON.parse(localStorage.getItem(TOURS_CACHE_STORAGE_KEY)) || {};
        const cachedEntry = cachedTours[slug] || null;
        if (!cachedEntry) return null;

        const cachedAt = Number(cachedEntry.cachedAt ?? 0);
        if (cachedAt && Date.now() - cachedAt > TOURS_CACHE_MAX_AGE_MS) {
          return null;
        }

        return cachedEntry.data || null;
      } catch (error) {
        console.warn("No se pudo leer toursCache; se fuerza refetch.", error);
        return null;
      }
    };

    const setTourInCache = (slug, tourData) => {
      try {
        const cachedTours =
          JSON.parse(localStorage.getItem(TOURS_CACHE_STORAGE_KEY)) || {};
        cachedTours[slug] = {
          cachedAt: Date.now(),
          data: tourData,
        };
        localStorage.setItem(
          TOURS_CACHE_STORAGE_KEY,
          JSON.stringify(cachedTours)
        );
      } catch (error) {
        console.warn("No se pudo guardar toursCache.", error);
      }
    };

    const getTourBySlug = async (slug) => {
      try {
        console.log("Llamando a /toursAPI/get-tour-by-slug con slug:", slug);
        if (!axiosInstance) {
          console.warn("axiosInstance es null o undefined");
          return null;
        }
        const response = await axiosInstance.get("/toursAPI/get-tour-by-slug", {
          params: { slug, duration: urlDuration },
        });
        console.log("Respuesta del servidor:", response);

        if (response.data) {
          return normalizeTourData(response.data, slug);
        } else {
          console.log("No hay response.data, se redirige a /404");
          navigate("/404");
          return null;
        }
      } catch (error) {
        console.error("Error fetching tour:", error);
        navigate("/404");
        return null;
      }
    };
    const fetchTour = async () => {
      if (tour) return; // ya lo tienes cargado

      /* ── 1. ¿Hay caché? ────────────────────────────── */
      const cached = getTourFromCache(slug);
      console.log("Caché encontrada!!!");
      console.log(cached);
      if (cached && isMounted) {
        const normalizedCached = normalizeTourData(cached, slug);
        if (
          normalizedCached?.id &&
          !hasInvalidPeopleRanges(normalizedCached?.durations)
        ) {
          setTour(normalizedCached);
          return;
        }
        console.warn(
          "Ignorando toursCache por datos obsoletos/invalidos; se fuerza refetch."
        );
      }

      /* ── 2. No hay caché → peticion al backend ─────── */
      const fetched = await getTourBySlug(slug);
      if (fetched && isMounted) {
        setTourInCache(slug, fetched);
        setTour(fetched);
      } else if (!fetched && isMounted) {
        navigate("/404");
      }
    };

    if (!axiosInstance) {
      console.warn(
        "axiosInstance es null o undefined, esperando inicialización..."
      );
      return;
    }

    fetchTour();

    return () => {
      isMounted = false;
    };
  }, [slug, tour, axiosInstance, navigate]);

  // =============================================
  // Actualizar la cita en step=3
  // =============================================
  useEffect(() => {
    if (step === 3 && selectedDate && selectedTime) {
      const [hourPart, minutePart = "0"] = String(selectedTime).split(":");
      const appointmentDateTime = DateTime.fromMillis(selectedDate * 1000, {
        zone: "Europe/Madrid",
      }).set({
        hour: parseInt(hourPart, 10) || 0,
        minute: parseInt(minutePart, 10) || 0,
        second: 0,
        millisecond: 0,
      });
      setAppointmentDate(appointmentDateTime.toJSDate());
    }
  }, [step, selectedDate, selectedTime, setAppointmentDate]);

  // =============================================
  // Scroll top cuando cambie step
  // =============================================
  useEffect(() => {
    scrollToTop();
  }, [step]);

  // =============================================
  // Bloquear por completo la navegación "atrás"
  // =============================================
  useEffect(() => {
    if (isEmbeddedReservationFlow()) {
      historyEntryIdRef.current += 1;
      window.history.replaceState(
        {
          ...(window.history.state || {}),
          reservationCloseSentinel: true,
          step: 0,
          reservationStep: 0,
        },
        "",
        createReservationStepHistoryUrl(0, historyEntryIdRef.current)
      );
      writeStepHistory(1, { mode: "push" });
      return;
    }

    writeStepHistory(1);
  }, [writeStepHistory]);

  useEffect(() => {
    const handlePopState = (event) => {
      if (event?.state?.reservationCloseSentinel) {
        requestParentClose();
        return;
      }

      const stateStep = event?.state?.reservationStep ?? event?.state?.step;
      if (typeof stateStep === "number" && stateStep >= 1 && stateStep <= 3) {
        if (stateStep <= 2) {
          setSelectedTime(null);
        }
        setStep(stateStep);
        return;
      }

      if (stepRef.current > 1) {
        goToPreviousStep();
        return;
      }

      requestParentClose();
    };

    const handleBackButton = (event) => {
      if (stepRef.current <= 1) {
        if (requestParentClose()) {
          event?.preventDefault?.();
        }
        return;
      }

      event?.preventDefault?.();
      goToPreviousStep({ preferBrowserHistory: true });
    };

    // Añade los listeners
    window.addEventListener("popstate", handlePopState);
    document.addEventListener("backbutton", handleBackButton, false);

    // Cleanup al desmontar
    return () => {
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("backbutton", handleBackButton, false);
    };
  }, [goToPreviousStep, requestParentClose]);

  useEffect(() => {
    const handleParentMessage = (event) => {
      if (event?.data?.action === "bookingGoBack") {
        goToPreviousStep();
      }
    };

    window.addEventListener("message", handleParentMessage);
    return () => window.removeEventListener("message", handleParentMessage);
  }, [goToPreviousStep]);

  // =============================================
  // ENVIAR STEP AL PADRE (POSTMESSAGE)
  // Cada vez que step cambie, notificamos a window.parent
  // =============================================
  useEffect(() => {
    // Verifica que sea un iframe (window.parent !== window)
    // para evitar problemas en modo local
    if (window.parent !== window) {
      window.parent.postMessage({ action: "stepChange", payload: step }, "*");
    }
  }, [step]);

  // =============================================
  // Funciones para avanzar en el flujo
  // =============================================
  const handleDateSelect = (epochTime) => {
    setSelectedDate(epochTime);
    goToStep(2, { historyMode: "push" });
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    goToStep(3, { historyMode: "push" });
  };

  // Manejo de "atrás" en botones internos (si lo usas)
  const handleBack = () => {
    goToPreviousStep({ preferBrowserHistory: true });
  };

  const closeReservationFlow = useCallback(() => {
    if (requestParentClose()) {
      return;
    }

    navigate(-1);
  }, [navigate, requestParentClose]);

  // =============================================
  // Framer-motion
  // =============================================
  const pageVariants = {
    generalOut: {
      opacity: 0,
      transition: { duration: 0.6 },
    },
  };

  const pageTransition = {
    type: "spring",
    stiffness: 300,
    damping: 30,
  };

  // =============================================
  // Render
  // =============================================
  if (!tour) {
    return <div>{t("reservationCheck", "Consultando disponibilidad...")}</div>;
  }

  const reservationManagerStyle =
    isPhone && step === 3
      ? {
          "--mobile-sticky-drawer-clearance":
            "var(--sticky-drawer-closed-clearance, calc(112px + env(safe-area-inset-bottom, 0px)))",
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          scrollPaddingBottom: "var(--mobile-sticky-drawer-clearance)",
        }
      : undefined;

  return (
    <div
      className={isPhone ? "reservationManager mobile" : "reservationManager"}
      style={reservationManagerStyle}
    >
      <header className="reservationManager-header">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
              className="preScreen"
            >
              <ReservationCalendar
                onDaySelect={handleDateSelect}
                onBack={closeReservationFlow}
                isPhone={isPhone}
                tourUUID={tour.id}
                selectedDuration={tour?.duration}
              />
            </motion.div>
          )}
          {step === 2 && (
            <motion.div
              key="step2"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
              className="preScreen"
            >
              <TimeSelection
                date={selectedDate}
                onTimeSelect={handleTimeSelect}
                onBack={handleBack}
                isPhone={isPhone}
                tourUUID={tour.id}
                selectedDuration={tour?.duration}
              />
            </motion.div>
          )}
          {step === 3 && (
            <motion.div
              key="step3"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
              className={
                isPhone && step === 3
                  ? "preScreen preScreen-confirmation"
                  : "preScreen"
              }
            >
              <ConfirmationScreen
                date={DateTime.fromMillis(selectedDate * 1000, {
                  zone: "Europe/Madrid",
                }).toISODate()}
                time={selectedTime}
                onBack={handleBack}
                formData={formData}
                setFormData={setFormData}
                secondsEpoch={selectedDate}
                isPhone={isPhone}
                tourDuration={tour.duration}
                slug={slug}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </div>
  );
};

const WrappedReservationManager = () => (
  <AppointmentProvider>
    <ReservationManager />
  </AppointmentProvider>
);

export { AppointmentContext, WrappedReservationManager as default };
